import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import useI18n from 'hooks/useI18n'
import { ReadCompetitiveIntelligenceMarket } from 'services/competitive-intelligence'
import { trackEvent, trackEventOnce } from 'services/analytics'
import { apiRequest, exposeStrapiError } from 'utils'

const LANGUAGE_LOCALES = {
  Chinese: 'zh-CN', English: 'en-US', German: 'de-DE', Hindi: 'hi-IN',
  Japanese: 'ja-JP', Portuguese: 'pt-BR', Spanish: 'es-ES',
}
const errorStatus = result => Number(result?.error?.status || 0)
const defaultPeriod = () => 'all'

export default function useController() {
  const { language, t } = useI18n()
  const history = useHistory()
  const location = useLocation()
  const requestSequence = useRef(0)
  const [state, setState] = useState({
    status: 'loading', items: [], pagination: { total: 0, hasMore: false },
    facets: { competitors: [], platforms: [], types: [] }, loadingMore: false,
  })
  const filters = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const tab = ['events', 'news', 'social'].includes(params.get('tab')) ? params.get('tab') : 'events'
    return {
      tab,
      competitor: params.get('competitor') || '',
      period: params.get('period') || defaultPeriod(tab),
      platform: params.get('platform') || '',
      type: params.get('type') || '',
      q: params.get('q') || '',
    }
  }, [location.search])
  const [query, setQuery] = useState(filters.q)

  useEffect(() => setQuery(filters.q), [filters.q])

  const updateUrl = useCallback((name, value, replace = false) => {
    const params = new URLSearchParams(location.search)
    if (value) params.set(name, value)
    else params.delete(name)
    history[replace ? 'replace' : 'push']({ pathname: location.pathname, search: params.toString() })
  }, [history, location.pathname, location.search])

  useEffect(() => {
    if (query === filters.q) return undefined
    const timer = setTimeout(() => updateUrl('q', query, true), 420)
    return () => clearTimeout(timer)
  }, [filters.q, query, updateUrl])

  const fail = useCallback((result, reason) => {
    const code = errorStatus(result)
    if (result?.error && ![401, 403].includes(code)) exposeStrapiError(result)
    trackEvent('competitive_intelligence_market_load_failed', { reason })
    setState(current => ({
      ...current,
      status: [401, 403].includes(code) ? 'denied' : code >= 500 || !result ? 'unavailable' : 'error',
      loadingMore: false,
    }))
  }, [])

  const load = useCallback(async ({ append = false, cursor = '' } = {}) => {
    const sequence = ++requestSequence.current
    setState(current => append ? { ...current, loadingMore: true } : { ...current, status: 'loading', items: [], loadingMore: false })
    const result = await apiRequest(() => ReadCompetitiveIntelligenceMarket({
      kind: filters.tab,
      competitor: filters.competitor,
      period: filters.period,
      platform: filters.tab === 'social' ? filters.platform : '',
      type: filters.tab === 'news' ? filters.type : '',
      q: filters.q,
      cursor,
      limit: 24,
    }))
    if (sequence !== requestSequence.current) return
    if (!result || result?.error) {
      fail(result, `market_${errorStatus(result) || 'unavailable'}`)
      return
    }
    const data = result.data || {}
    const items = Array.isArray(data.items) ? data.items : []
    setState(current => ({
      status: append || items.length ? 'ready' : 'empty',
      items: append ? [...current.items, ...items] : items,
      pagination: data.pagination || { total: 0, hasMore: false },
      facets: {
        competitors: Array.isArray(data.facets?.competitors) ? data.facets.competitors : [],
        platforms: Array.isArray(data.facets?.platforms) ? data.facets.platforms : [],
        types: Array.isArray(data.facets?.types) ? data.facets.types : [],
      },
      loadingMore: false,
    }))
    trackEventOnce('competitive_intelligence_market_viewed', {
      competitive_section: filters.tab, result_count: Number(data.pagination?.total || 0),
    }, `market:${location.search}`)
    if (filters.q) trackEvent(Number(data.pagination?.total || 0) > 0 ? 'COMPETITIVE_SEARCH_PERFORMED' : 'COMPETITIVE_SEARCH_NO_RESULTS', {
      competitive_section: filters.tab, query_length: filters.q.trim().length,
      result_count: Number(data.pagination?.total || 0), competitor_id: filters.competitor || undefined,
    })
  }, [fail, filters, location.search])

  useEffect(() => { load() }, [load])

  const onTabChange = useCallback(tab => {
    history.push({ pathname: location.pathname, search: `tab=${tab}&period=${defaultPeriod(tab)}` })
  }, [history, location.pathname])
  const onFilterChange = useCallback((name, value) => {
    trackEvent(value && value !== 'all' ? 'COMPETITIVE_FILTER_APPLIED' : 'COMPETITIVE_FILTER_REMOVED', {
      competitive_section: filters.tab, filter_type: name, filter_value: value || 'all',
    })
    updateUrl(name, value)
  }, [filters.tab, updateUrl])
  const onReset = useCallback(() => {
    trackEvent('COMPETITIVE_FILTER_REMOVED', {
      competitive_section: filters.tab, filter_type: 'all', filter_value: 'all',
    })
    setQuery('')
    history.push({ pathname: location.pathname, search: `tab=${filters.tab}&period=${defaultPeriod(filters.tab)}` })
  }, [filters.tab, history, location.pathname])

  const locale = LANGUAGE_LOCALES[language] || 'en-US'
  const formatCount = useMemo(() => new Intl.NumberFormat(locale).format, [locale])
  const formatDate = useCallback(value => {
    if (!value) return ''
    const parsed = new Date(`${value.length === 7 ? `${value}-01` : value}T00:00:00`)
    return Number.isNaN(parsed.getTime()) ? value : new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(parsed)
  }, [locale])
  const competitorOptions = useMemo(() => [
    { id: 'all', title: t('competitive_intelligence_research_competitor_all') },
    ...state.facets.competitors.map(item => ({ id: item.key, title: `${item.label} (${formatCount(item.count)})` })),
  ], [formatCount, state.facets.competitors, t])
  const platformOptions = useMemo(() => [
    { id: 'all', title: t('competitive_intelligence_market_all_platforms') },
    ...state.facets.platforms.map(item => ({ id: item.key, title: `${item.label} (${formatCount(item.count)})` })),
  ], [formatCount, state.facets.platforms, t])
  const typeOptions = useMemo(() => [
    { id: 'all', title: t('competitive_intelligence_market_all_types') },
    ...state.facets.types.map(item => ({ id: item.key, title: `${item.label} (${formatCount(item.count)})` })),
  ], [formatCount, state.facets.types, t])
  const periodOptions = useMemo(() => filters.tab === 'events' ? [
    { id: 'all', title: t('competitive_intelligence_period_all_events') },
    { id: 'upcoming', title: t('competitive_intelligence_period_upcoming') },
  ] : filters.tab === 'social' ? [
    { id: 'all', title: t('competitive_intelligence_period_all') },
    { id: '7d', title: t('competitive_intelligence_period_7d') },
    { id: '30d', title: t('competitive_intelligence_period_30d') },
    { id: '90d', title: t('competitive_intelligence_period_90d') },
  ] : [
    { id: 'all', title: t('competitive_intelligence_period_all') },
    { id: '30d', title: t('competitive_intelligence_period_30d') },
    { id: '90d', title: t('competitive_intelligence_period_90d') },
    { id: '12m', title: t('competitive_intelligence_period_12m') },
  ], [filters.tab, t])

  return {
    ...state,
    activeTab: filters.tab,
    competitorOptions,
    filters,
    formatCount,
    formatDate,
    onFilterChange,
    onLoadMore: () => load({ append: true, cursor: state.pagination.nextCursor }),
    onQueryChange: setQuery,
    onReset,
    onTabChange,
    periodOptions,
    platformOptions,
    query,
    retry: load,
    t,
    typeOptions,
  }
}
